import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { AppId } from '../../../../api-client/openapi/models';
import { AfRoutes } from '../../routes';
import { AppIds } from '../apps/appIds';
import { AppsService } from '../apps/apps.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { CustomerService } from '../../customers/services/customer.service';

@Injectable({
  providedIn: 'root',
})
export class RoutingService {
  private readonly appsService = inject(AppsService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly customerService = inject(CustomerService);

  private readonly appsPriorityOrder: ReadonlyArray<{ appId: AppId; route: AfRoutes }> = [
    { appId: AppIds.CarBodyPlanner, route: AfRoutes.carBodyPlannerDashboard },
    { appId: AppIds.FeasibilityAi, route: AfRoutes.feasibilityAiDashboard },
    { appId: AppIds.Acls, route: AfRoutes.cloudLicenseServerDashboard },
  ];

  constructor(private router: Router) {}

  public getPostLoginRoute(): Observable<AfRoutes> {
    return this.getFirstAvailableAppRoute().pipe(
      map((route) => route ?? AfRoutes.welcomePage)
    );
  }

  public navigateToDashboard(): void {
    this.getPostLoginRoute().pipe(take(1)).subscribe((route) => {
      this.router.navigate([route]);
    });
  }

  private getFirstAvailableAppRoute(): Observable<AfRoutes | undefined> {
    return this.appsService.getAvailableAppIds().pipe(
      map((availableAppIds) =>
        this.appsPriorityOrder.find(({ appId }) => availableAppIds.includes(appId))?.route
      ),
      switchMap((matchingRoute) => {
        if (matchingRoute) {
          return of(matchingRoute);
        }

        // If no matching app route, check if app stream is enabled
        return this.authenticationService.customerId$.pipe(
          take(1),
          switchMap((customerId) => {
            if (!customerId) {
              return of(undefined);
            }

            return this.customerService.getCustomerById(customerId).pipe(
              map((customer) => {
                return customer.appStreamEnabled
                  ? AfRoutes.cloudApplicationDashboard
                  : undefined;
              })
            );
          })
        );
      })
    );
  }
}
